import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useQuery } from '@apollo/client';
import {
  useWizardService,
  getChannelList,
  useFields,
  ComparatorContext,
} from '@kizi/comparator-core';
import { Search, SearchWrapperProps } from '@kizi/comparator-components';

export interface SearchItem {
  id: any;
  label: string;
}
export interface SpecificChannelSearchProps extends SearchWrapperProps {
  _fieldName: string;
  optionsFieldName: string;
  queriesFieldName?: string;
  defaultFiltersFieldName?: string;
  _category: string;
  id?: string;
  onChange: Function;
}

export const SpecificChannelSearch: React.FC<SpecificChannelSearchProps> = ({
  optionsFieldName,
  queriesFieldName,
  _category,
  id,
  _fieldName,
  defaultFiltersFieldName,
  onChange,
  ...props
}) => {
  const { api } = useContext(ComparatorContext);
  const { data } = useQuery(getChannelList, {
    context: {
      uri: `${process.env.KIZI_PUBLIC_DOMAIN}/triple-play/graphql`,
      headers: {
        'Authorization':
          'Bearer ' +
          (api.credentials.token || localStorage.getItem('accessToken')),
      },
    },
  });
  const service = useWizardService();
  const [field, storedOptions, defaultFiltersField] = useFields([
    _fieldName,
    optionsFieldName,
    queriesFieldName ?? '',
    defaultFiltersFieldName ?? '',
  ]);

  const parsedValues = useMemo(
    () =>
      field?.value !== undefined ? JSON.parse(field?.value as string) : [],
    [field?.value],
  );
  const [values, setValues] = useState(id ? parsedValues[id] : parsedValues);

  if (!storedOptions || !field) return null;
  const storedOptionsValue: SearchItem[] =
    storedOptions?.hasOwnProperty('value') && storedOptions.value !== undefined
      ? JSON.parse(storedOptions.value as string)
      : [];

  const [options, setOptions] = useState<SearchItem[]>(storedOptionsValue);

  const renameObjectProperties = (arr) => {
    return arr.map(({ name, id }) => {
      return {
        label: name,
        id,
      };
    });
  };

  const handleRes = ({ channelList }) => {
    const { category } = channelList.television;
    const channels: SearchItem[] = [];

    category.forEach((cat) => {
      channels.push(...renameObjectProperties(cat.channel));
    });

    return channels.sort((a, b) => a.label.localeCompare(b.label));
  };

  useEffect(() => {
    if (data) {
      setOptions(handleRes(data));
    }
  }, [data]);

  useEffect(() => {
    if (field?.value === defaultFiltersField?.value && id) {
      setValues(parsedValues[id]);
    }
  }, [parsedValues]);

  const handleChange = (options) => {
    const value = id
      ? JSON.stringify({ ...parsedValues, [id]: options })
      : JSON.stringify(options);
    field?.set({ value: value });
    onChange(id, options);
    setValues(options);
  };

  useEffect(() => {
    if (id) {
      onChange(id, values);

      service?.send([
        {
          type: 'CHANGE',
          target: {
            name: 'filters',
            value: JSON.stringify({ ...parsedValues, [id]: values }),
          },
        },
      ]);
    }
  }, [values]);

  return (
    <Search
      {...props}
      options={options}
      onChange={handleChange}
      value={values}
    />
  );
};
